<template>
    <sidebar @close="$emit('close')" v-if="plan">
        <template v-slot:title>
            {{ plan.display_name }}
        </template>

        <template v-slot:header>
            <div class="row mb-1">
                <div class="col">
                    <span class="text-muted">{{ company.name }}</span>
                </div>
            </div>

            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabEarningContentMain" id="tabEarningLinkMain" data-toggle="tab" role="tab" aria-controls="tabEarningContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabEarningContentMain" role="tabpanel" aria-labelledby="tabEarningLinkMain">

                    <info-widget :collapsible="false">
                        <template v-slot:title>Plan Information</template>

                        <template v-slot:headerbutton>
                            <button v-if="!plan.is_past" type="button" class="btn btn-outline-primary mr-1" @click.prevent="deactivateModalOpen = true">
                                {{ plan.ends_on ? 'Change Deact. Date' : 'Deactivate' }}
                            </button>

                            <button type="button" class="btn btn-outline-primary" @click.prevent="editModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="row mb-0">
                                    <div class="col">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ plan.plan_type_pretty }}</span>
                                            <label>Plan Type</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ plan.provider_name }}</span>
                                            <label>Provider</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ plan.plan_number }}</span>
                                            <label>Plan Number</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ plan.company_deduction_type.name }}</span>
                                            <label>Deduction Type</label>
                                        </div>

                                        <div class="mui-textfield" v-if="plan.catchup_company_deduction_type">
                                            <span class="textfield">{{ plan.catchup_company_deduction_type.name }}</span>
                                            <label>Catch-Up Deduction Type</label>
                                        </div>

                                        <div class="mui-textfield" v-if="plan.hpi_catchup_company_deduction_type">
                                            <span class="textfield">{{ plan.hpi_catchup_company_deduction_type.name }}</span>
                                            <label>HPI Catch-Up Deduction Type</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ $filters.date(plan.starts_on) }} &ndash; {{ plan.ends_on ? $filters.date(plan.ends_on) : 'Indefinite' }}</span>
                                            <label>Plan Period</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ plan.money_manager_pretty }}</span>
                                            <label>Money Manager</label>
                                        </div>

                                        <div class="mui-textfield" v-if="plan.money_manager == 'paymasters'">
                                            <span class="textfield">{{ plan.routing_number }}</span>
                                            <label>Routing Number</label>
                                        </div>

                                        <div class="mui-textfield" v-if="plan.money_manager == 'paymasters'">
                                            <span class="textfield">x{{ plan.account_number_last_4 }}</span>
                                            <label>Account Number (last 4)</label>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <match-widget
                        :plan="plan"
                        :collapsible="false"
                        @edit="editEmployerMatchModalOpen = true"
                    />

                    <info-widget :collapsible="false">
                        <template v-slot:title>Contribution Basis ({{ plan.deferred_comp_plan_earning_types?.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editEarningTypesModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="plan.deferred_comp_plan_earning_types?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Earning Types</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="eRel in plan.deferred_comp_plan_earning_types" :key="eRel.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ eRel.company_earning_type.name }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                No earning types are configured, so contributions are calculated on all real earnings including tips.
                            </div>
                        </slot>
                    </info-widget>

                    <info-widget :collapsible="false">
                        <template v-slot:title>Positions ({{ plan.deferred_comp_plan_positions?.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositionsModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="plan.deferred_comp_plan_positions?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Positions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="pRel in plan.deferred_comp_plan_positions" :key="pRel.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ pRel.position.terminal_value }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This plan is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-modal
            v-if="editModalOpen"
            :plan="plan"
            :client="client"
            @updated="onUpdate"
            @close="editModalOpen = false"
        />

        <deactivate-modal
            v-if="deactivateModalOpen"
            :plan="plan"
            :client="client"
            @updated="onUpdate"
            @close="deactivateModalOpen = false"
        />

        <positions-modal
            v-if="editPositionsModalOpen"
            :plan="plan"
            :client="client"
            @updated="onUpdate"
            @close="editPositionsModalOpen = false"
        />

        <employer-match-modal
            v-if="editEmployerMatchModalOpen"
            :plan="plan"
            :client="client"
            @updated="onUpdate"
            @close="editEmployerMatchModalOpen = false"
        />

        <earning-types-modal
            v-if="editEarningTypesModalOpen"
            :plan="plan"
            :client="client"
            @updated="onUpdate"
            @close="editEarningTypesModalOpen = false"
        />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditModal from './EditModal'
import DeactivateModal from './DeactivateModal'
import PositionsModal from './PositionsModal'
import EarningTypesModal from './EarningTypesModal'
import EmployerMatchModal from './EmployerMatchModal'
import MatchWidget from './MatchWidget'

export default {
    props: ['planId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditModal, DeactivateModal, PositionsModal, EarningTypesModal, EmployerMatchModal, MatchWidget, },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.plan.company_id)
        },
    },
    data() {
        return {
            plan: null,
            activeTab: this.tab || 'main',
            editModalOpen: false,
            deactivateModalOpen: false,
            editPositionsModalOpen: false,
            editEarningTypesModalOpen: false,
            editEmployerMatchModalOpen: false,
        }
    },
    watch: {
        planId() {
            this.getData()
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/deferred-comp-plans/${this.planId}`).then(resp => {
                this.plan = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(obj) {
            if (obj) {
                Object.assign(this.plan, obj)
                this.$emit('updated')
            }
            else {
                this.getData()
            }
        },
    },
}
</script>
